/**
 * Client schedule use cases – get my schedule, mark workout completed.
 */
import { coachClientRepo } from '../../data/repositories/CoachClientRepository';
import { clientScheduleRepo } from '../../data/repositories/ClientScheduleRepository';
import { scheduleDayRepo } from '../../data/repositories/ScheduleDayRepository';
import { assignedWorkoutRepo } from '../../data/repositories/AssignedWorkoutRepository';
import { coachWorkoutRepo } from '../../data/repositories/CoachWorkoutRepository';
import { workoutCompletionRepo } from '../../data/repositories/WorkoutCompletionRepository';

export async function getMyCoachId(clientId) {
  return coachClientRepo.getMyCoachId(clientId);
}

/**
 * Get schedule days and assigned workouts for a date range.
 * Returns { schedule, days: [ { day, assigned: [ { assigned, workout } ] } ] }
 */
export async function getMySchedule(clientId, fromDate, toDate) {
  const schedule = await clientScheduleRepo.getByClient(clientId);
  if (!schedule) return { schedule: null, days: [] };
  const days = await scheduleDayRepo.listByClientSchedule(schedule.id, clientId, fromDate, toDate);
  const result = [];
  for (const day of days) {
    const assigned = await assignedWorkoutRepo.listByScheduleDay(day.id, clientId);
    const withWorkouts = await Promise.all(
      assigned.map(async (a) => {
        const workout = await coachWorkoutRepo.getById(a.coachWorkoutId);
        return { assigned: a, workout: workout || { title: 'Unknown' } };
      })
    );
    result.push({ day, assigned: withWorkouts });
  }
  return { schedule, days: result };
}

/**
 * Mark an assigned workout as completed. Idempotent (one completion per assigned workout).
 */
export async function markWorkoutCompleted(clientId, assignedWorkoutId) {
  const existing = await workoutCompletionRepo.getByAssignedWorkout(assignedWorkoutId, clientId);
  if (existing) return existing.id;
  return workoutCompletionRepo.create({
    assignedWorkoutId,
    clientId,
    completedAt: new Date(),
  });
}

export async function getMyCompletions(clientId, limitCount = 500) {
  return workoutCompletionRepo.listByClient(clientId, limitCount);
}

export function isWorkoutCompleted(completions, assignedWorkoutId) {
  return completions.some((c) => c.assignedWorkoutId === assignedWorkoutId);
}
